import React, { useState } from "react";
import { Row, Col, Table, Button, Badge } from "react-bootstrap";
import Plot from "react-plotly.js";

const SystemsConnectionTrend = ({ data }) => {

  const [mode, setMode] = useState("count");
  const [page, setPage] = useState(1);

  const rowsPerPage = 5;

  if (!data) return null;

  const parsed = JSON.parse(data);
  const rows = Array.isArray(parsed) ? parsed : Object.values(parsed);

  // -------------------------
  // DATE PARSER
  // -------------------------

  const parseDate = (d) => {
    const [day, month, year] = d.split("/");
    return new Date(`${year}-${month}-${day}`);
  };

  // -------------------------
  // SORT DATA
  // -------------------------

  rows.sort((a, b) => parseDate(b.date) - parseDate(a.date));

  const dates = rows.map(r => parseDate(r.date));

  // -------------------------
  // WEEKLY TICKS IF MANY DAYS
  // -------------------------

  const useWeeklyTicks = dates.length > 50;

  const getWeekStart = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff));
  };

  let tickvals;
  let ticktext;

  if (useWeeklyTicks) {

    const weeks = {};

    dates.forEach(d => {

      const monday = getWeekStart(d);
      const key = monday.toISOString();

      if (!weeks[key]) {
        weeks[key] = monday;
      }

    });

    tickvals = Object.values(weeks);

    ticktext = tickvals.map(d =>
      d.toLocaleDateString("it-IT")
    );

  } else {

    tickvals = dates;
    ticktext = rows.map(r => r.date);

  }

  // -------------------------
  // BUILD SERIES
  // -------------------------

  const allSeries = rows.map(r => {
    if (mode === "percent") {
      return r.total ? ( (r.active / r.total) * 100 ).toFixed(2): 0;
    }
    return r.active;
  });

  const laserSeries = rows.map(r => {
    if (mode === "percent") {
      return r.laser_total ? ((r.laser_active / r.laser_total) * 100 ).toFixed(2) : 0;
    }
    return r.laser_active;
  });

  const automationSeries = rows.map(r => {
    if (mode === "percent") {
      return r.automation_total ? ( (r.automation_active / r.automation_total) * 100 ).toFixed(2): 0;
    }
    return r.automation_active;
  });

  const maxValue = Math.max(
    ...allSeries,
    ...laserSeries,
    ...automationSeries
    );

  const yRange = mode === "percent"
    ? [0, 100]
    : [0, maxValue * 1.1];
  // -------------------------
  // TRACES
  // -------------------------

  const traces = [

    {
      x: dates,
      y: allSeries,
      type: "scatter",
      mode: "lines+markers",
      name: "All",
      line: { color: "#1f77b4", width: 3 },
      marker: { size: 7 }
    },

    {
      x: dates,
      y: laserSeries,
      type: "scatter",
      mode: "lines+markers",
      name: "Laser",
      line: { color: "#2ca02c", width: 3 },
      marker: { size: 7 }
    },

    {
      x: dates,
      y: automationSeries,
      type: "scatter",
      mode: "lines+markers",
      name: "Automation",
      line: { color: "#ff7f0e", width: 3 },
      marker: { size: 7 }
    }

  ];

  // -------------------------
  // PAGINATION
  // -------------------------

  const totalPages = Math.ceil(rows.length / rowsPerPage);

  const paginatedRows = rows.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  // -------------------------
  // LAYOUT
  // -------------------------

  const layout = {

    height: 420,

    margin: { t: 80, l: 60, r: 20, b: 100 },

    xaxis: {
      title: "Date",
      type: "date",

      tickmode: "array",
      tickvals: tickvals,
      ticktext: ticktext,

      tickangle: -45,
      automargin: true,

      range: [dates[dates.length - 1], dates[0]]
    },

    yaxis: {
    title: mode === "percent"
        ? "Active Devices (%)"
        : "Active Devices",

    range: yRange,
    rangemode: "tozero"
    },

    legend: {
      orientation: "h",
      x: 0.5,
      xanchor: "center",
      y: 1.15
    },

    hovermode: "x unified"

  };

  // -------------------------
  // RENDER
  // -------------------------

  return (

    <>

      {/* TITLE */}

      <Row className="mt-1 mb-4">
        <Col md={{ span: 8, offset: 2 }}>
          <h4 className="text-center">
            Trend giornaliero
          </h4>
        </Col>
      </Row>

      {/* MODE BUTTONS */}

      <Row className="mb-3">
        <Col className="text-center">

          <Button
            variant={mode === "count" ? "primary" : "outline-primary"}
            onClick={() => setMode("count")}
            className="me-2"
          >
            Count
          </Button>

          <Button
            variant={mode === "percent" ? "primary" : "outline-primary"}
            onClick={() => setMode("percent")}
          >
            %
          </Button>

        </Col>
      </Row>

      {/* CHART */}

      <Row>
        <Col>

          <Plot
            data={traces}
            layout={layout}
            style={{ width: "100%" }}
            useResizeHandler={true}
            config={{ responsive: true }}
          />

        </Col>
      </Row>


        {/* TABLE */}

        <Row className="mt-4">
        <Col md={{ span: 10, offset: 1 }}>

            <Table striped bordered hover>

            <thead>
                <tr className="text-center">
                <th>Date</th>
                <th>Active</th>
                <th>Inactive</th>
                <th>Never Active</th>
                <th>Δ Active vs Previous Day</th>
                </tr>
            </thead>

            <tbody>

                {paginatedRows.map((r, i) => {

                const total = r.total;

                const pActive = total ? ((r.active / total) * 100).toFixed(2) : 0;
                const pInactive = total ? ((r.inactive / total) * 100).toFixed(2) : 0;
                const pNever = total ? ((r.never_active / total) * 100).toFixed(2) : 0;

                // previous row
                const prev = rows[rows.findIndex(x => x.date === r.date) + 1];

                let delta = null;
                let deltaPercent = null;

                if (prev) {
                    delta = r.active - prev.active;
                    deltaPercent = prev.active
                    ? ((delta / prev.active) * 100).toFixed(1)
                    : 0;
                }

                let variant = "secondary";
                let symbol = "–";

                if (delta > 0) {
                    variant = "success";
                    symbol = "▲";
                }

                if (delta < 0) {
                    variant = "danger";
                    symbol = "▼";
                }

                return (

                    <tr key={r.date} className="align-middle text-center">

                    <td>
                        <b>{r.date}</b>
                        <div style={{ fontSize: "12px", color: "#777" }}>
                        {total} devices
                        <br />
                        Laser: {r.laser_total} | Auto: {r.automation_total}
                        </div>
                    </td>

                    <td>
                        <Badge bg="success">
                        {r.active} ({pActive}%)
                        </Badge>
                        <div style={{ fontSize: "11px", color: "#777" }}>
                        L:{r.laser_active} A:{r.automation_active}
                        </div>
                    </td>

                    <td>
                        <Badge bg="warning" text="dark">
                        {r.inactive} ({pInactive}%)
                        </Badge>
                        <div style={{ fontSize: "11px", color: "#777" }}>
                        L:{r.laser_inactive} A:{r.automation_inactive}
                        </div>
                    </td>

                    <td>
                        <Badge bg="danger">
                        {r.never_active} ({pNever}%)
                        </Badge>
                        <div style={{ fontSize: "11px", color: "#777" }}>
                        L:{r.laser_never_active} A:{r.automation_never_active}
                        </div>
                    </td>

                    <td>

                        {prev ? (

                        <Badge bg={variant}>

                            {symbol} {delta} ({deltaPercent}%)

                        </Badge>

                        ) : (

                        <Badge bg="secondary">N/A</Badge>

                        )}

                    </td>

                    </tr>

                )

                })}

            </tbody>

            </Table>

            {/* PAGINATION */}

            <Row className="mt-3">
            <Col className="text-center">

                <Button
                variant="outline-secondary"
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
                className="me-2"
                >
                Prev
                </Button>

                <span style={{ margin: "0 10px" }}>
                Page {page} / {totalPages}
                </span>

                <Button
                variant="outline-secondary"
                disabled={page === totalPages}
                onClick={() => setPage(page + 1)}
                >
                Next
                </Button>

            </Col>
            </Row>

        </Col>
        </Row>

    </>

  );

};

export default SystemsConnectionTrend;